import {
  AnalyticsConfig,
  AnalyticsReporter,
} from '@stripe-internal/analytics-js';
import {Timestamp} from 'src/utils/timestamp';
import type {Timestamp as TimestampType} from 'src/utils/timestamp';

const LOAD_TIMESTAMP = new Timestamp();

// keep these in sync with https://git.corp.stripe.com/stripe-internal/zoolander/blob/f9de7e98cf59adeca65c50331dbc9644c701c3a3/src/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/client_config.yaml#L3013
type LogEvents =
  | 'crypto_payin_session.render.error'
  | 'crypto_payin_session.render.loaded'
  | 'crypto_payin_session.pay_button_pressed'
  | 'crypto_payin_session.link.logout_after_redacted_email'
  | 'crypto_payin_session.transaction_signing_error'
  | 'crypto_payin_session.transaction_signing_abandoned'
  | 'crypto_payin_session.wallet_connect.render_error'
  | 'crypto_payin_session.link.render_error'
  | 'crypto_payin_session.link.consumer_wallet_lookup_error'
  | 'crypto_payin_session.wallet_verification_error'
  | 'crypto_payin_session.wallet_disconnect_error'
  | 'crypto_payin_session.wallet.selected'
  | 'crypto_payin_session.wallet.connected'
  | 'crypto_payin_session.wallet.verifying'
  | 'crypto_payin_session.wallet.verified'
  | 'crypto_payin_session.wallet_connection.ready'
  | 'crypto_payin_session.info'
  | 'crypto_payin_session.error'
  | 'crypto_payin_session.setup_button_pressed'
  | 'crypto_payin_session.screen.rendered';

// Only log safe params that don't contain PIIs. If a param isn't safe it will be redacted so no point to log it
// Keep these in sync with zoolander/src/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/redaction_rules_config.yaml
const SAFE_PARAMS = [
  'mode',
  'payin_session_id',
  'logger_id',
  'message',
  'optional_params',
  'duration',
  'since_page_start',
  'error_message',
  'screen',
  'error_code',
  'error',
  'error_type',
  'error_context',
  // Wallet params
  'provider',
  'address',
  'network',
  'currency',
  'wallet_key',
  // Debug
  'navigation_type',
  'render_loaded_count',
] as const;

type SafeParamKey = (typeof SAFE_PARAMS)[number];

type Params = Partial<Record<SafeParamKey, unknown>>;

export class Logger {
  analytics: AnalyticsReporter<LogEvents>;

  clientId: string;

  initialized: boolean;

  eventCount: number;

  isProduction: boolean;

  loadTimestamp: TimestampType;

  baseParams: Params;

  constructor(initialParams: Params = {}) {
    this.clientId = 'crypto-payin';
    this.initialized = false;
    this.eventCount = 0;
    this.baseParams = {};
    this.isProduction = process.env.NODE_ENV === 'production';
    this.loadTimestamp = LOAD_TIMESTAMP;

    const aelConfig: AnalyticsConfig = {
      clientId: this.clientId,
      inProduction: this.isProduction,
      batchSettings: {
        enableBatching: true,
      },
      safeParams: SAFE_PARAMS.reduce((acc, param) => {
        acc[param] = true;
        return acc;
      }, {} as Record<SafeParamKey, boolean>),
    };

    this.analytics = new AnalyticsReporter<LogEvents>(aelConfig);

    this.initialize(initialParams);
  }

  private initialize(initialParams: Params = {}) {
    if (this.initialized) {
      return this;
    }

    this.initialized = true;
    this.baseParams = {
      logger_id: crypto.randomUUID(),
      ...initialParams,
    };
    return this;
  }

  updateBaseParams(newParams: Params) {
    this.baseParams = {
      ...this.baseParams,
      ...newParams,
    };
  }

  log(eventName: LogEvents, eventData?: Params) {
    this.eventCount += 1;

    const eventDataWithDuration = {
      ...eventData,
      duration: this.loadTimestamp.getElapsedTime(),
    };

    const params = {
      ...this.baseParams,
      ...eventDataWithDuration,
    };

    if (!this.isProduction) {
      console.log(eventName, params);
      return;
    }

    this.analytics.track(eventName, params);
  }

  info(message?: unknown, ...optionalParams: unknown[]) {
    this.log('crypto_payin_session.info', {
      message,
      optional_params: optionalParams,
    });
  }
}

const singletonLogger = new Logger();

export function getLogger(): Logger {
  return singletonLogger;
}

export function useLogger(): Logger {
  return singletonLogger;
}

export {singletonLogger as logger};
